"use client";

import Link from "next/link";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { identifyUser } from "@/components/posthog-provider";
import { analytics, getLeadAttribution } from "@/lib/analytics";
import { markHomeInlineOfferSubmitted } from "@/lib/home-inline-offer-session";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { ROMA_CLUB_SMS_MARKETING_CONSENT_LABEL } from "@/lib/roma-club-sms-marketing";
import { cn } from "@/lib/utils";

type SubscribeSource =
  | "newsletter"
  | "birthday_club"
  | "website"
  | "gaming"
  | "cove_club"
  | "roma_club"
  | "events"
  | "menu";

type SubscribeResponse = {
  success?: boolean;
  isNew?: boolean;
  subscriberId?: string;
  error?: string;
};

interface HomeInlineOfferCaptureProps {
  className?: string;
  source?: SubscribeSource;
  eyebrow?: string;
  heading?: string;
  description?: string;
  submitLabel?: string;
  privacyNote?: string;
  successEyebrow?: string;
  successHeading?: string;
  successDescription?: string;
  /** Roma Club homepage: optional marketing SMS opt-in (10DLC MARKETING). */
  enableRomaMarketingSmsOptIn?: boolean;
  /** Tenant-specific recurring marketing SMS consent contract. */
  marketingSmsOptIn?: {
    consentLabel: string;
    consentCopyVersion: string;
    consentSourcePath: string;
  };
  /** Full homepage section vs. embedded card (Roma dark funnel). */
  layout?: "section" | "embedded";
  /** Styling for embedded dark-funnel card. */
  theme?: "light" | "dark-funnel";
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizePhoneDigits(phone: string) {
  return phone.replace(/\D/g, "");
}

const offerLabelClassLight =
  "text-sm font-semibold leading-snug text-[var(--roma-espresso)]/82";
const offerInputClassLight =
  "min-h-12 rounded-xl border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] px-4 text-base text-[var(--roma-espresso)] shadow-none placeholder:text-[var(--roma-espresso)]/60 focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[var(--roma-terracotta)]/35 disabled:bg-[var(--roma-linen)]/55 disabled:opacity-80";
const offerLabelClassDark =
  "text-sm font-semibold leading-snug text-white/90";
const offerInputClassDark =
  "min-h-12 rounded-xl border border-white/18 bg-black/25 px-4 text-base text-white shadow-none placeholder:text-white/45 focus-visible:border-[var(--primary)] focus-visible:ring-[3px] focus-visible:ring-[var(--primary)]/35 disabled:opacity-70";

export function HomeInlineOfferCapture({
  className,
  source = "cove_club",
  eyebrow = "Cove Club Newsletter",
  heading = "Join The Exclusive Yard.",
  description = "Get patio night invites, happy hour notes, game-day reminders, and first access to exclusive Tuscan Cove offers by email.",
  submitLabel = "Join The Exclusive Yard",
  privacyNote = "Email is used for The Exclusive Yard newsletter. Unsubscribe anytime.",
  successEyebrow = "You are in The Exclusive Yard",
  successHeading = "Watch your inbox for The Exclusive Yard.",
  successDescription = "Your Cove Club newsletter signup is confirmed. Watch your inbox for patio nights, happy hour notes, event invitations, and exclusive offers when they go live.",
  enableRomaMarketingSmsOptIn = source === "roma_club",
  marketingSmsOptIn,
  layout = "section",
  theme = "light",
}: HomeInlineOfferCaptureProps) {
  const { location } = useMarketingLocationContext();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsMarketingConsent, setSmsMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const marketingSmsConfig =
    marketingSmsOptIn ??
    (enableRomaMarketingSmsOptIn
      ? {
          consentLabel: ROMA_CLUB_SMS_MARKETING_CONSENT_LABEL,
          consentCopyVersion: "roma-club-marketing-2026-07-07-v1",
          consentSourcePath: "/#rewards",
        }
      : undefined);
  const offerLabelClass =
    theme === "dark-funnel" ? offerLabelClassDark : offerLabelClassLight;
  const offerInputClass =
    theme === "dark-funnel" ? offerInputClassDark : offerInputClassLight;
  const mutedTextClass =
    theme === "dark-funnel"
      ? "text-[13px] leading-relaxed text-[#d4d4d4] sm:text-sm sm:leading-[1.6]"
      : "text-[11px] leading-4 text-[var(--roma-espresso)]/66 sm:text-xs sm:leading-relaxed";
  const consentTextClass =
    theme === "dark-funnel"
      ? "flex items-start gap-3 text-[13px] leading-relaxed text-[#d4d4d4] sm:text-sm sm:leading-[1.6]"
      : "flex items-start gap-3 text-[11px] leading-4 text-[var(--roma-espresso)]/72 sm:text-xs sm:leading-relaxed";
  const trackingLocation =
    layout === "embedded" && source === "roma_club"
      ? "home_dark_funnel_roma_club"
      : source === "cove_club"
        ? "home_inline_cove_club"
        : source === "roma_club"
          ? "home_inline_roma_club"
          : "home_inline_happy_hour_offer";

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedFirstName = firstName.trim();
    const trimmedEmail = email.trim().toLowerCase();

    setError(null);

    if (!trimmedFirstName || !trimmedEmail) {
      setError("Please enter your first name and email address.");
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    const trimmedPhone = phone.trim();
    if (
      marketingSmsConfig &&
      smsMarketingConsent &&
      normalizePhoneDigits(trimmedPhone).length < 10
    ) {
      setError("Please enter a valid mobile number to receive promotional texts.");
      return;
    }

    setIsSubmitting(true);
    analytics.formSubmitted(trackingLocation);

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmedEmail,
          firstName: trimmedFirstName,
          phone:
            marketingSmsConfig && smsMarketingConsent
              ? trimmedPhone
              : undefined,
          smsMarketingConsent: Boolean(
            marketingSmsConfig && smsMarketingConsent,
          ),
          smsMarketingConsentSource:
            marketingSmsConfig && smsMarketingConsent
              ? marketingSmsConfig.consentSourcePath
              : undefined,
          smsMarketingConsentCopyVersion:
            marketingSmsConfig && smsMarketingConsent
              ? marketingSmsConfig.consentCopyVersion
              : undefined,
          source,
          attribution: getLeadAttribution(trackingLocation),
        }),
      });
      const result = (await response.json()) as SubscribeResponse;

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Subscription failed");
      }

      setIsSuccess(true);
      markHomeInlineOfferSubmitted(location.id);
      const leadProperties = {
        lead_id: result.subscriberId,
        lead_type: "subscriber",
        source: trackingLocation,
      };
      analytics.subscribeSuccess(result.isNew ?? false, leadProperties);
      analytics.leadCaptured("subscriber", trackingLocation, leadProperties);
      identifyUser(trimmedEmail, {
        firstName: trimmedFirstName,
        source: trackingLocation,
        subscribed_at: new Date().toISOString(),
      });
    } catch (submissionError) {
      console.error("Inline offer capture error:", submissionError);
      setError("We could not add you right now. Please try again in a moment.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSuccess) {
    const successBody = (
      <div className="min-w-0">
        <p
          className={
            theme === "dark-funnel"
              ? "text-sm font-semibold uppercase tracking-[0.12em] text-white/85"
              : "roma-eyebrow text-[var(--roma-terracotta)]"
          }
        >
          <span>{successEyebrow}</span>
        </p>
        <h2
          className={
            theme === "dark-funnel"
              ? "mt-2 font-serif text-[clamp(1.35rem,3.5vw,1.75rem)] font-bold leading-tight"
              : "mt-3 font-serif text-[clamp(1.85rem,6vw,2.35rem)] font-normal leading-[1.1] tracking-tight"
          }
        >
          {successHeading}
        </h2>
        <p
          className={
            theme === "dark-funnel"
              ? "mt-3 text-sm leading-relaxed text-[var(--muted-foreground)]"
              : "mt-4 max-w-2xl text-sm leading-relaxed text-[var(--roma-espresso)]/68"
          }
        >
          {successDescription}
        </p>
      </div>
    );

    if (layout === "embedded") {
      return (
        <div data-email-capture-trigger="inline-offer" className={className}>
          {successBody}
        </div>
      );
    }

    return (
      <section
        data-email-capture-trigger="inline-offer"
        className={cn(
          "border-y border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 px-4 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20",
          className,
        )}
      >
        <div className="mx-auto flex max-w-7xl items-start gap-4 rounded-[14px] border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] p-6 text-[var(--roma-espresso)] shadow-[0_18px_54px_-42px_rgba(43,28,17,0.4)] sm:p-8 lg:items-center">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[var(--roma-terracotta)]/25 bg-[var(--roma-terracotta)]/10 text-[var(--roma-terracotta)]">
            <span
              className="h-2.5 w-2.5 rounded-full bg-[var(--roma-terracotta)]"
              aria-hidden="true"
            />
          </span>
          {successBody}
        </div>
      </section>
    );
  }

  const formFields = (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "space-y-4",
        layout === "section" ? "p-6 sm:p-8 lg:flex-1" : "mt-6",
      )}
      noValidate
    >
      <div
        className={cn(
          "grid gap-4",
          layout === "embedded"
            ? "grid-cols-1"
            : "sm:grid-cols-2 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_auto] lg:items-end",
        )}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="home-offer-first-name" className={offerLabelClass}>
            First name
          </Label>
          <Input
            id="home-offer-first-name"
            name="firstName"
            type="text"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            className={offerInputClass}
            placeholder="Maria"
            autoComplete="given-name"
            disabled={isSubmitting}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="home-offer-email" className={offerLabelClass}>
            Email address
          </Label>
          <Input
            id="home-offer-email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={offerInputClass}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            disabled={isSubmitting}
            required
          />
        </div>
        {marketingSmsConfig ? (
          <div
            className={cn(
              "flex flex-col gap-1.5",
              layout === "section" && "sm:col-span-2 lg:col-span-3",
            )}
          >
            <Label htmlFor="home-offer-phone" className={offerLabelClass}>
              Mobile phone{" "}
              <span className="font-normal opacity-70">(optional)</span>
            </Label>
            <Input
              id="home-offer-phone"
              name="phone"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className={offerInputClass}
              placeholder="(702) 555-0100"
              autoComplete="tel"
              inputMode="tel"
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        {!marketingSmsConfig ? (
          <Button
            type="submit"
            className={cn(
              theme === "dark-funnel"
                ? "roma-funnel-btn-primary min-h-12 w-full"
                : "min-h-12 w-full rounded-xl bg-[var(--roma-terracotta)] px-6 text-sm font-semibold text-[var(--roma-espresso)] shadow-[0_18px_45px_-34px_oklch(0.36_0.08_50_/_0.52)] transition-transform hover:bg-[var(--roma-saffron-deep)] active:translate-y-px sm:col-span-2 lg:col-span-1 lg:w-auto lg:shrink-0 lg:px-7",
            )}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="mr-2 h-2 w-2 animate-pulse rounded-full bg-current"
                  aria-hidden="true"
                />
                Joining
              </>
            ) : (
              submitLabel
            )}
          </Button>
        ) : null}
      </div>

      {/* 10DLC: consent must appear above submit so carriers can verify CTA without interacting first */}
      {marketingSmsConfig ? (
        <>
          <label className={consentTextClass}>
            <input
              type="checkbox"
              name="smsMarketingConsent"
              checked={smsMarketingConsent}
              onChange={(event) => setSmsMarketingConsent(event.target.checked)}
              disabled={isSubmitting}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--roma-parchment-line)]"
              aria-label={`${marketingSmsConfig.consentLabel} View our Privacy Policy and Terms.`}
            />
            <span>
              {marketingSmsConfig.consentLabel} View our{" "}
              <Link href="/privacy" className="underline underline-offset-2">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link href="/terms" className="underline underline-offset-2">
                Terms
              </Link>
              .
            </span>
          </label>
          <Button
            type="submit"
            className={cn(
              theme === "dark-funnel"
                ? "roma-funnel-btn-primary min-h-12 w-full"
                : "min-h-12 w-full rounded-xl bg-[var(--roma-terracotta)] px-6 text-sm font-semibold text-[var(--roma-espresso)] shadow-[0_18px_45px_-34px_oklch(0.36_0.08_50_/_0.52)] transition-transform hover:bg-[var(--roma-saffron-deep)] active:translate-y-px",
            )}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="mr-2 h-2 w-2 animate-pulse rounded-full bg-current"
                  aria-hidden="true"
                />
                Joining
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </>
      ) : null}

      <div aria-live="polite" className="min-h-4">
        {error ? (
          <p
            role="alert"
            className={cn(
              "flex items-start gap-2 text-sm",
              theme === "dark-funnel"
                ? "text-[#fca5a5]"
                : "text-[var(--roma-wine)]",
            )}
          >
            <span
              className={cn(
                "mt-2 h-1.5 w-1.5 shrink-0 rounded-full",
                theme === "dark-funnel"
                  ? "bg-[#fca5a5]"
                  : "bg-[var(--roma-wine)]",
              )}
              aria-hidden="true"
            />
            {error}
          </p>
        ) : (
          <p className={mutedTextClass}>{privacyNote}</p>
        )}
      </div>
    </form>
  );

  if (layout === "embedded") {
    return (
      <div
        id={enableRomaMarketingSmsOptIn ? "rewards" : undefined}
        data-email-capture-trigger="inline-offer"
        className={className}
      >
        {formFields}
      </div>
    );
  }

  return (
    <section
      id={enableRomaMarketingSmsOptIn ? "rewards" : undefined}
      data-email-capture-trigger="inline-offer"
      className={cn(
        "border-y border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 px-4 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20",
        className,
      )}
    >
      <div className="mx-auto max-w-7xl overflow-hidden rounded-[14px] border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] text-[var(--roma-espresso)] shadow-[0_18px_54px_-42px_rgba(43,28,17,0.4)] lg:flex lg:items-stretch">
        <div className="border-b border-[var(--roma-parchment-line)] p-6 sm:p-8 lg:w-[min(100%,19.5rem)] lg:shrink-0 lg:border-b-0 lg:border-r xl:w-[min(100%,22rem)]">
          <p className="roma-eyebrow text-[var(--roma-terracotta)]">
            <span>{eyebrow}</span>
          </p>
          <h2 className="mt-3 max-w-xl font-serif text-[clamp(1.85rem,6vw,2.35rem)] font-normal leading-[1.1] tracking-tight">
            {heading}
          </h2>
          <p className="mt-4 hidden max-w-sm text-sm leading-relaxed text-[var(--roma-espresso)]/68 sm:block">
            {description}
          </p>
        </div>

        {formFields}
      </div>
    </section>
  );
}
